import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SchoolIcon from '@mui/icons-material/School';
import BarChartIcon from '@mui/icons-material/BarChart';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PaymentsIcon from '@mui/icons-material/Payments';
import GroupsIcon from '@mui/icons-material/Groups';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import api from '../services/api';
import { Event, Registration } from '../types';
import { formatDate, formatRegistrationNumber, formatCurrency } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';

const COLORS = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#14b8a6'];

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { showError } = useNotification();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [activeEvents, setActiveEvents] = useState<Event[]>([]);
  const [recentRegistrations, setRecentRegistrations] = useState<Registration[]>([]);
  const [unpaidRegistrations, setUnpaidRegistrations] = useState<Registration[]>([]);
  const [statistics, setStatistics] = useState<any>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch active events
      const eventsResponse = await api.get('/api/reference/events?status=ACTIVE');
      const activeEventsData = eventsResponse.data || [];
      setActiveEvents(activeEventsData);

      // Fetch recent registrations (last 10)
      if (activeEventsData.length > 0) {
        const eventId = activeEventsData[0].id;
        
        // Get recent registrations
        const registrationsResponse = await api.get('/api/registrations', {
          params: { eventId, limit: 10, page: 1 },
        });
        setRecentRegistrations(registrationsResponse.data.registrations || []);

        // Get unpaid registrations
        const unpaidResponse = await api.get('/api/registrations', {
          params: { eventId, paymentStatus: 'UNPAID', limit: 10, page: 1 },
        });
        setUnpaidRegistrations(unpaidResponse.data.registrations || []);

        // Get statistics for first active event
        try {
          const statsResponse = await api.get('/api/statistics', {
            params: { eventId },
          });
          setStatistics(statsResponse.data);
        } catch (error) {
          console.error('Error fetching statistics:', error);
        }
      }
    } catch (error: any) {
      console.error('Error fetching dashboard data:', error);
      showError(error.response?.data?.error || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'success';
      case 'PERFORMANCE_PAID':
      case 'DIPLOMAS_PAID':
        return 'warning';
      case 'UNPAID':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPaymentStatusLabel = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'Оплачено';
      case 'PERFORMANCE_PAID':
        return 'Выступление оплачено';
      case 'DIPLOMAS_PAID':
        return 'Дипломы оплачены';
      case 'UNPAID':
        return 'Не оплачено';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Приветствие и текущее мероприятие */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
          {user?.name ? `Здравствуйте, ${user.name}` : 'Здравствуйте'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {activeEvents.length > 0
            ? `Активное мероприятие: ${activeEvents[0].name} (${formatDate(activeEvents[0].startDate)} — ${formatDate(activeEvents[0].endDate)})`
            : 'Нет активных мероприятий — создайте его в разделе «Администрирование»'}
        </Typography>
      </Box>

      {/* Быстрые действия */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {(() => {
          const quickActions = [
            { title: 'Новая регистрация', subtitle: 'Записать участника на выступление', icon: <PersonAddIcon sx={{ fontSize: 32 }} />, path: '/registrations/new', roles: ['ADMIN', 'REGISTRATOR'], color: '#8b5cf6' },
            { title: 'Приём оплаты', subtitle: 'Оплата за выступление или дипломы', icon: <AccountBalanceWalletIcon sx={{ fontSize: 32 }} />, path: '/combined-payment', roles: ['ADMIN', 'REGISTRATOR', 'ACCOUNTANT'], color: '#10b981' },
            { title: 'Учёт оплат', subtitle: 'Проверить поступления и отчёты', icon: <ReceiptIcon sx={{ fontSize: 32 }} />, path: '/accounting', roles: ['ADMIN', 'ACCOUNTANT'], color: '#f59e0b' },
            { title: 'Дипломы и медали', subtitle: 'Начислить и распечатать', icon: <SchoolIcon sx={{ fontSize: 32 }} />, path: '/diplomas', roles: ['ADMIN', 'REGISTRATOR'], color: '#ec4899' },
            { title: 'Статистика', subtitle: 'Сводка по номинациям и дисциплинам', icon: <BarChartIcon sx={{ fontSize: 32 }} />, path: '/statistics', roles: ['ADMIN', 'STATISTICIAN', 'ACCOUNTANT'], color: '#3b82f6' },
          ].filter((a) => !user || a.roles.includes(user.role));
          return quickActions.map((action) => (
            <Grid item xs={6} sm={4} md={2.4} key={action.title}>
              <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider', '&:hover': { borderColor: 'primary.main', boxShadow: 4 } }}>
                <CardActionArea onClick={() => navigate(action.path)} sx={{ height: '100%', p: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
                  <Avatar sx={{ bgcolor: action.color, width: 44, height: 44, mb: 1.5, color: '#fff' }}>
                    {action.icon}
                  </Avatar>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {action.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {action.subtitle}
                  </Typography>
                </CardActionArea>
              </Card>
            </Grid>
          ));
        })()}
      </Grid>

      {/* Активные мероприятия */}
      {activeEvents.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {activeEvents.map((event) => (
            <Grid item xs={12} sm={6} md={4} key={event.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {event.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(event.startDate)} - {formatDate(event.endDate)}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 2 }}
                    onClick={() => navigate(`/registrations?eventId=${event.id}`)}
                  >
                    Перейти к регистрациям
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Ключевые цифры по активному мероприятию */}
      {statistics && activeEvents.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <KpiCard icon={<GroupsIcon fontSize="small" />} label="Всего регистраций" value={statistics.overview?.totalRegistrations || 0} color="primary.main" />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard icon={<EventAvailableIcon fontSize="small" />} label="Коллективов" value={statistics.overview?.totalCollectives || 0} color="info.main" />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard icon={<PaymentsIcon fontSize="small" />} label="Сумма оплат" value={formatCurrency(statistics.payments?.paidAmount || 0)} color="success.main" />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard icon={<WarningAmberIcon fontSize="small" />} label="Ждут оплаты" value={statistics.payments ? `${(statistics.overview?.totalRegistrations || 0) - (statistics.payments.paid || 0)} шт.` : '—'} color="warning.main" />
          </Grid>
        </Grid>
      )}

      {/* Графики */}
      {statistics && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {statistics.byNomination && statistics.byNomination.length > 0 && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  По номинациям
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statistics.byNomination}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {statistics.byNomination.map((_entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          )}

          {statistics.byDiscipline && statistics.byDiscipline.length > 0 && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  По дисциплинам
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={statistics.byDiscipline}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* Последние регистрации */}
      {recentRegistrations.length > 0 && (
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Последние регистрации</Typography>
            <Button size="small" onClick={() => navigate('/registrations')}>
              Все регистрации
            </Button>
          </Box>
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {recentRegistrations.map((reg: any) => (
                <Box
                  key={reg.id}
                  sx={{
                    border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5,
                    cursor: 'pointer', '&:active': { bgcolor: 'action.selected' },
                  }}
                  onClick={() => navigate(`/registrations/${reg.id}`)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                      {reg.collective?.name || '-'}
                    </Typography>
                    <Chip
                      label={getPaymentStatusLabel(reg.paymentStatus)}
                      color={getPaymentStatusColor(reg.paymentStatus) as any}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                    {formatRegistrationNumber(reg)} • {reg.danceName || 'Без названия'} • {formatDate(reg.createdAt)}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>№</TableCell>
                    <TableCell>Коллектив</TableCell>
                    <TableCell>Название</TableCell>
                    <TableCell>Статус оплаты</TableCell>
                    <TableCell>Дата</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentRegistrations.map((reg: any) => (
                    <TableRow
                      key={reg.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/registrations/${reg.id}`)}
                    >
                    <TableCell>{formatRegistrationNumber(reg)}</TableCell>
                      <TableCell>{reg.collective?.name || '-'}</TableCell>
                      <TableCell>{reg.danceName || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={getPaymentStatusLabel(reg.paymentStatus)}
                          color={getPaymentStatusColor(reg.paymentStatus) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(reg.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Неоплаченные регистрации */}
      {unpaidRegistrations.length > 0 && (
        <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" color="error" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Неоплаченные регистрации ({unpaidRegistrations.length})
            </Typography>
            <Button size="small" onClick={() => navigate('/registrations?paymentStatus=UNPAID')}>
              Все неоплаченные
            </Button>
          </Box>
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {unpaidRegistrations.map((reg: any) => (
                <Box
                  key={reg.id}
                  sx={{
                    border: '1px solid', borderColor: 'error.main', borderRadius: 2, p: 1.5,
                    cursor: 'pointer', '&:active': { bgcolor: 'action.selected' },
                    borderLeft: '3px solid #f59e0b',
                  }}
                  onClick={() => navigate(`/registrations/${reg.id}`)}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                    {reg.collective?.name || '-'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', mt: 0.25 }}>
                    {formatRegistrationNumber(reg)} • {reg.danceName || 'Без названия'} • {formatDate(reg.createdAt)}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>№</TableCell>
                    <TableCell>Коллектив</TableCell>
                    <TableCell>Название</TableCell>
                    <TableCell>Дата</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unpaidRegistrations.map((reg: any) => (
                    <TableRow
                      key={reg.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/registrations/${reg.id}`)}
                    >
                    <TableCell>{formatRegistrationNumber(reg)}</TableCell>
                      <TableCell>{reg.collective?.name || '-'}</TableCell>
                      <TableCell>{reg.danceName || '-'}</TableCell>
                      <TableCell>{formatDate(reg.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {activeEvents.length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Нет активных мероприятий
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Создайте мероприятие в админ-панели, чтобы начать работу
          </Typography>
          {user?.role === 'ADMIN' && (
            <Button variant="contained" onClick={() => navigate('/admin')}>
              Перейти в админ-панель
            </Button>
          )}
        </Paper>
      )}
    </Box>
  );
};

// Карточка ключевого показателя с акцентной цифрой и иконкой
interface KpiCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ icon, label, value, color }) => (
  <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', color }}>
          {icon}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {label}
        </Typography>
      </Stack>
      <Typography variant="h5" component="div" sx={{ fontWeight: 700, color, mt: 0.5 }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

